import type { LegalBlock, HeadingBlock, ListBlock, ListItemBlock } from '../models/ast';
import { getStylesRegistry } from '../styles/registry';
import type { ParagraphStyle, NumberingScheme } from '../models/styles';

export type NumberingEntry = { displayNumber: string; nestingLevel: number };
export type NumberingMap = Record<string, NumberingEntry>;

/**
 * Computes simple, deterministic numbering for Clause, Heading, and ListItem blocks.
 * - Restarts numbering on Section/SectionBreak boundaries
 * - Clause: decimal sequence per section (1, 2, 3, ...)
 * - Heading: ordinal per level per section (level-specific counters)
 * - ListItem: ordinal within its parent List (1-based); nestingLevel reflects list depth
 *
 * This is intentionally minimal and deterministic; formats such as (a), i., 1.1 are future work.
 */
export function resolveNumbering(ast: LegalBlock[]): NumberingMap {
  const map: NumberingMap = {};

  let clauseCounter = 0;
  const headingCountersByLevel: number[] = []; // 1-based index by level
  const registry = getStylesRegistry();

  function resetSection(): void {
    clauseCounter = 0;
    for (const k of Object.keys(headingCountersByLevel)) {
      delete headingCountersByLevel[Number(k)];
    }
  }

  function toAlpha(n: number): string { // 1 -> a, 27 -> aa
    let num = Math.max(1, n);
    let s = '';
    while (num > 0) {
      const rem = (num - 1) % 26;
      s = String.fromCharCode(97 + rem) + s;
      num = Math.floor((num - 1) / 26);
    }
    return s;
  }

  function toRoman(n: number): string {
    const romans: Array<[number, string]> = [
      [1000, 'm'], [900, 'cm'], [500, 'd'], [400, 'cd'],
      [100, 'c'], [90, 'xc'], [50, 'l'], [40, 'xl'],
      [10, 'x'], [9, 'ix'], [5, 'v'], [4, 'iv'], [1, 'i']
    ];
    let num = Math.max(1, n);
    let out = '';
    for (const [val, sym] of romans) {
      while (num >= val) { out += sym; num -= val; }
      if (num === 0) break;
    }
    return out;
  }

  function formatValue(value: number, scheme?: NumberingScheme): string {
    let core = String(value);
    switch (scheme?.type) {
      case 'alpha': core = toAlpha(value); break;
      case 'roman': core = toRoman(value); break;
      case 'decimal':
      default: core = String(value);
    }
    if (scheme?.format) return scheme.format.replace('{value}', core);
    return core;
  }

  function getParagraphStyle(id?: string): ParagraphStyle | undefined {
    return id ? registry.paragraphStyles[id] : undefined;
  }

  function resolveHeadingNumber(level: number, styleId?: string): string {
    // Ensure array size
    for (let i = headingCountersByLevel.length; i < level; i++) headingCountersByLevel[i] = 0;
    // Increment this level and reset deeper levels
    headingCountersByLevel[level] = (headingCountersByLevel[level] || 0) + 1;
    for (let i = level + 1; i < headingCountersByLevel.length; i++) headingCountersByLevel[i] = 0;
    // Build hierarchical string using style-driven scheme if present
    const ps = getParagraphStyle(styleId);
    const parts: string[] = [];
    for (let i = 1; i <= level; i++) {
      const scheme = ps?.numberingScheme?.[`level${i}`];
      const val = headingCountersByLevel[i] || 0;
      parts.push(formatValue(val, scheme));
    }
    return parts.join('.');
  }

  // Type guards for safety
  function isHeading(node: LegalBlock): node is HeadingBlock { return node.type === 'Heading'; }
  function isList(node: LegalBlock): node is ListBlock { return node.type === 'List'; }
  function isListItem(node: any): node is ListItemBlock { return node && node.type === 'ListItem'; }
  function isListBlock(node: any): node is ListBlock { return node && node.type === 'List'; }

  function visit(node: LegalBlock, listDepth: number = 0): void {
    switch (node.type) {
      case 'Section':
      case 'SectionBreak':
        resetSection();
        break;
      case 'Clause': {
        clauseCounter += 1;
        map[node.id] = { displayNumber: String(clauseCounter), nestingLevel: 0 };
        break;
      }
      case 'Heading': {
        const level = isHeading(node) ? (node as HeadingBlock).attrs?.level ?? 1 : 1;
        const display = resolveHeadingNumber(level, node.styleId);
        map[node.id] = { displayNumber: display, nestingLevel: level };
        break;
      }
      case 'List': {
        if (!isList(node)) break;
        const list = node;
        const items = list.children || [];
        for (let i = 0; i < items.length; i++) {
          const li = items[i];
          if (!isListItem(li)) continue;
          // Determine numbering scheme for this list level (prefer item style, then list style)
          const levelKey = `level${listDepth + 1}`;
          const itemPs = getParagraphStyle(li.styleId);
          const listPs = getParagraphStyle(list.styleId);
          const scheme = (itemPs?.numberingScheme && itemPs.numberingScheme[levelKey])
            || (listPs?.numberingScheme && listPs.numberingScheme[levelKey]);
          const display = formatValue(i + 1, scheme);
          map[li.id] = { displayNumber: display, nestingLevel: listDepth + 1 };
          // Recurse into nested lists within list item children (native recursive lists)
          for (const ch of li.children || []) {
            if (isListBlock(ch)) {
              visit(ch as unknown as LegalBlock, listDepth + 1);
            }
          }
        }
        break;
      }
      default:
        break;
    }
  }

  for (const node of ast) {
    visit(node, 0);
  }

  return map;
}


